/**
 * The intake queue's placeholder (#11422 round 19, R8), shown at /documents
 * on a deployment that reviews PHI documents while the app or the page's code
 * loads, where any other deployment shows the documents product's.
 *
 * It draws the page that follows it: the top bar with the title and the two
 * verbs at the right, the views strip, the search and its controls, and the
 * table's head and rows. A document's address draws the review page's frame
 * instead: the bar, then the file beside the decision panel. Still, like the
 * documents product's placeholder: nothing spins.
 */
import React from "react";

const bar = "block rounded-[4px] bg-[var(--fp-ink-wash)]";

const ROW_WIDTHS = [168, 136, 184, 150, 120, 176, 142, 160, 128, 172];

const TopBar: React.FC = () => (
  <div
    data-skeleton="intake-topbar"
    className="flex shrink-0 items-center gap-3 border-b border-[var(--fp-border)] px-[var(--fp-page-gutter)] py-1.5"
    style={{ background: "var(--fp-topbar-ground)" }}
  >
    {/* At phone the bar leads with the menu button, and the quiet verb and
      the count yield to the one primary verb (PageTopbar). */}
    <span
      data-skeleton="intake-menu"
      className="h-[30px] w-[30px] shrink-0 rounded-[7px] bg-[var(--fp-ink-wash)] md:hidden"
    />
    <span className={`${bar} h-4 w-[92px] shrink-0`} />
    <span
      data-skeleton="intake-count"
      className={`${bar} h-3 w-[72px] shrink-0 max-md:hidden`}
    />
    <span
      data-skeleton="intake-quiet-verb"
      className="ml-auto h-[30px] w-[110px] rounded-[7px] border border-[var(--fp-border)] max-md:hidden"
    />
    <span
      data-skeleton="intake-primary-verb"
      className="h-[30px] w-[96px] shrink-0 rounded-[7px] bg-[var(--fp-ink-wash)] max-md:ml-auto"
    />
  </div>
);

const Queue: React.FC = () => (
  <div className="flex min-h-0 flex-1 flex-col gap-3 px-[var(--fp-page-gutter)] pt-4">
    <div
      data-skeleton="intake-views"
      className="flex items-center gap-2 overflow-hidden"
    >
      {[112, 84, 48, 96].map((width, index) => (
        <span
          key={index}
          className="h-[30px] shrink-0 rounded-[7px] bg-[var(--fp-ink-wash)]"
          style={{ width }}
        />
      ))}
    </div>
    <div className="flex items-center gap-2">
      <span className="h-[30px] w-full max-w-[240px] rounded-[7px] border border-[var(--fp-border)] bg-[var(--fp-surface)]" />
      <span className="h-[30px] w-[72px] shrink-0 rounded-[7px] border border-[var(--fp-border)]" />
      <span className="h-[30px] w-[88px] shrink-0 rounded-[7px] border border-[var(--fp-border)] max-md:hidden" />
    </div>
    <div
      data-skeleton="intake-table"
      className="min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)]"
    >
      <div className="flex h-10 items-center gap-6 border-b border-[var(--fp-border)] px-4">
        {[80, 64, 96, 56, 72].map((width, index) => (
          <span
            key={index}
            className={`${bar} h-2.5 ${index >= 2 ? "max-md:hidden" : ""}`}
            style={{ width }}
          />
        ))}
      </div>
      {ROW_WIDTHS.map((width, index) => (
        <div
          key={index}
          className="flex h-11 items-center gap-6 border-b border-[var(--fp-border)] px-4 last:border-b-0"
        >
          <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] bg-[var(--fp-ink-wash)]" />
          <span className={`${bar} h-3`} style={{ width }} />
          <span
            data-skeleton="intake-wide-column"
            className={`${bar} h-3 w-[120px] max-md:hidden`}
          />
          <span
            data-skeleton="intake-wide-column"
            className={`${bar} h-3 w-[72px] max-md:hidden`}
          />
        </div>
      ))}
    </div>
  </div>
);

const Review: React.FC = () => (
  <div className="flex min-h-0 flex-1 gap-3 p-3 max-md:flex-col">
    <div
      data-skeleton="intake-file"
      className="min-h-0 flex-1 rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)]"
    />
    <div
      data-skeleton="intake-decision"
      className="flex w-[360px] shrink-0 flex-col gap-3 rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)] p-4 max-md:w-full"
    >
      <span className={`${bar} h-4 w-[140px]`} />
      <span className={`${bar} h-3 w-[220px]`} />
      <span className={`${bar} h-3 w-[180px]`} />
    </div>
  </div>
);

/** `review`: a document's address (the review page), else the queue. */
const IntakeSkeletonLoader: React.FC<{ review: boolean }> = ({ review }) => (
  <div
    data-skeleton="intake"
    className="flex h-full w-full flex-col overflow-hidden bg-[var(--fp-bg)]"
    aria-hidden
  >
    <TopBar />
    {review ? <Review /> : <Queue />}
  </div>
);

export default IntakeSkeletonLoader;
